import { Button, Card, Checkbox, Form, InputNumber, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from './components/EducationPageChrome';
import { EducationStudentSelect } from './components/EducationLookupSelects';

type Component = { id: string; code: string; name: string; maximum_marks?: number | null };
type Scheme = { id: string; name: string; status: string; components: Component[] };
type Entry = {
  id: string; component_name: string; admission_no: string; first_name: string; last_name?: string;
  marks_raw: number; maximum_marks: number; status: string;
};

export default function AssessmentEntryPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scheme = useQuery({
    queryKey: ['education-assessment-scheme', id],
    queryFn: async () => (await api.get<Scheme>(`/education/assessments/schemes/${id}`)).data,
  });
  const entries = useQuery({
    queryKey: ['education-assessment-entries', id],
    queryFn: async () => (await api.get<Entry[]>(`/education/assessments/schemes/${id}/entries`)).data || [],
  });
  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => api.put(`/education/assessments/schemes/${id}/entries`, values),
    onSuccess: () => {
      message.success(t('saved'));
      queryClient.invalidateQueries({ queryKey: ['education-assessment-entries', id] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => message.error(error.response?.data?.message || t('unavailable')),
  });
  return (
    <EducationPageChrome
      title={t('assessmentEntry')}
      description={scheme.data?.name}
      extra={<Button onClick={() => navigate('/education/assessments')}>{t('back')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {scheme.data?.status !== 'published' ? (
          <Card size="small">
            <Form layout="inline" onFinish={(values) => save.mutate(values)}>
              <Form.Item name="student_id" rules={[{ required: true }]}><EducationStudentSelect style={{ width: 280 }} /></Form.Item>
              <Form.Item name="component_id" rules={[{ required: true }]}>
                <Select style={{ width: 260 }} placeholder={t('component')} options={(scheme.data?.components || []).map((c) => ({
                  value: c.id, label: `${c.code} — ${c.name}${c.maximum_marks == null ? ` (${t('notConfigured')})` : ''}`,
                  disabled: c.maximum_marks == null,
                }))} />
              </Form.Item>
              <Form.Item name="marks_raw" rules={[{ required: true }]}><InputNumber min={0} placeholder={t('marksRaw')} /></Form.Item>
              <Form.Item name="lock" valuePropName="checked"><Checkbox>{t('lockEntry')}</Checkbox></Form.Item>
              <Button type="primary" htmlType="submit" loading={save.isPending}>{t('save')}</Button>
            </Form>
          </Card>
        ) : null}
        <Card size="small">
          <Table rowKey="id" loading={entries.isLoading} dataSource={entries.data || []} columns={[
            { title: t('student'), render: (_, row: Entry) => `${row.admission_no} — ${row.first_name} ${row.last_name || ''}` },
            { title: t('component'), dataIndex: 'component_name' },
            { title: t('marksRaw'), dataIndex: 'marks_raw' },
            { title: t('maximumMarks'), dataIndex: 'maximum_marks' },
            { title: t('status'), dataIndex: 'status', render: (v: string) => <Tag>{v}</Tag> },
          ]} />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
